import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";
import { refusalDetail } from "../lib/refusalDetail";
import {
  DraftTemplate,
  DraftTemplateField,
  WorkPaperTemplateWithFieldsResponse,
  TemplateField,
  TemplateFieldResponse,
  TemplateType,
  CreateTemplateRequestSchema,
  UpdateTemplateRequestSchema,
  CreateFieldRequestSchema,
  UpdateFieldRequestSchema,
  ReorderFieldsRequest,
  GetTemplateResponseSchema,
  CreateTemplateResponseSchema,
  CreateFieldResponseSchema,
} from "../models/workPaperTemplate";

interface UseWorkPaperTemplateEditorOptions {
  templateId?: string | null;
  onSaveSuccess?: (templateId: string) => void;
  // A new template's type before the user picks one; null asks them to.
  defaultType?: TemplateType | null;
}

let tempIdCounter = 0;

function generateTempId(): string {
  return `temp-${Date.now()}-${tempIdCounter++}`;
}

function createEmptyDraftTemplate(type: TemplateType | null): DraftTemplate {
  return {
    id: null,
    name: "",
    type,
    fields: [],
    isBaseTemplate: false,
    workspaceId: null,
    isModified: false,
  };
}

function templateFieldToDraft(field: TemplateField): DraftTemplateField {
  return {
    ...field,
    isNew: false,
    isModified: false,
    isDeleted: false,
  };
}

function responseFieldToTemplateField(
  field: TemplateFieldResponse,
  index: number,
): TemplateField {
  return {
    ...field,
    fieldOrder: field.fieldOrder ?? index,
  };
}

function serverTemplateToDraft(
  template: WorkPaperTemplateWithFieldsResponse,
): DraftTemplate {
  return {
    id: template.id,
    name: template.name,
    type: template.type,
    fields: template.fields
      .map((field, index) =>
        templateFieldToDraft(responseFieldToTemplateField(field, index)),
      )
      .sort((a, b) => a.fieldOrder - b.fieldOrder),
    isBaseTemplate: template.isBaseTemplate ?? false,
    workspaceId: template.workspaceId ?? null,
    isModified: false,
  };
}

export function useWorkPaperTemplateEditor(
  options: UseWorkPaperTemplateEditorOptions = {},
) {
  const { templateId, onSaveSuccess, defaultType = null } = options;

  const [loading, setLoading] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftTemplate, setDraftTemplate] = useState<DraftTemplate>(() =>
    createEmptyDraftTemplate(defaultType),
  );
  const [originalTemplate, setOriginalTemplate] =
    useState<WorkPaperTemplateWithFieldsResponse | null>(null);

  // Track if there are unsaved changes
  const hasUnsavedChanges =
    draftTemplate.isModified ||
    draftTemplate.fields.some(
      (f) => f.isNew || f.isModified || f.isDeleted || f.isReordered,
    );

  const isReadOnly = draftTemplate.isBaseTemplate;

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load template from API
  const loadTemplate = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchFastApi({
        path: `/work-paper-templates/${id}`,
        method: "GET",
      });

      const parsed = GetTemplateResponseSchema.parse(response);
      const template = parsed.template;

      setOriginalTemplate(template);
      setDraftTemplate(serverTemplateToDraft(template));
    } catch (error) {
      console.error("Error loading template:", error);
      const errorMsg = "Failed to load template";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update template name
  const updateName = useCallback((name: string) => {
    setDraftTemplate((prev) => ({
      ...prev,
      name,
      isModified: true,
    }));
  }, []);

  // Update template type. Only a new template's: the type is fixed once the
  // template is created.
  const updateType = useCallback((type: TemplateType) => {
    setDraftTemplate((prev) =>
      prev.id === null ? { ...prev, type, isModified: true } : prev,
    );
  }, []);

  // Add new field
  const addField = useCallback(
    (fieldData: Omit<DraftTemplateField, "id" | "fieldOrder" | "isNew">) => {
      setDraftTemplate((prev) => {
        // Deleted fields stay in the draft until save, so a template whose
        // fields are all deleted still has fields here; Math.max() of none
        // is -Infinity, which serialises as null and fails the reorder.
        const activeOrders = prev.fields
          .filter((f) => !f.isDeleted)
          .map((f) => f.fieldOrder);
        const maxOrder =
          activeOrders.length > 0 ? Math.max(...activeOrders) : -1;

        const newField: DraftTemplateField = {
          ...fieldData,
          id: generateTempId(),
          fieldOrder: maxOrder + 1,
          isNew: true,
        };

        return {
          ...prev,
          fields: [...prev.fields, newField],
        };
      });
    },
    [],
  );

  // Update existing field
  const updateField = useCallback(
    (fieldId: string, updates: Partial<DraftTemplateField>) => {
      setDraftTemplate((prev) => ({
        ...prev,
        fields: prev.fields.map((field) =>
          field.id === fieldId
            ? { ...field, ...updates, isModified: !field.isNew }
            : field,
        ),
      }));
    },
    [],
  );

  // Delete field (mark as deleted)
  const deleteField = useCallback((fieldId: string) => {
    setDraftTemplate((prev) => {
      const field = prev.fields.find((f) => f.id === fieldId);

      // If it's a new field, remove it completely
      if (field?.isNew) {
        return {
          ...prev,
          fields: prev.fields.filter((f) => f.id !== fieldId),
        };
      }

      // Otherwise, mark as deleted
      return {
        ...prev,
        fields: prev.fields.map((f) =>
          f.id === fieldId ? { ...f, isDeleted: true } : f,
        ),
      };
    });
  }, []);

  // Reorder fields (for drag-and-drop)
  const reorderFields = useCallback((newOrder: string[]) => {
    setDraftTemplate((prev) => {
      const deletedFields = prev.fields.filter((f) => f.isDeleted);
      const orderedFields = newOrder
        .map((id, index) => {
          const field = prev.fields.find((f) => f.id === id);
          if (!field) {
            return null;
          }
          const result: DraftTemplateField = {
            ...field,
            fieldOrder: index,
          };
          if (field.fieldOrder !== index && !field.isNew) {
            result.isReordered = true;
          }
          return result;
        })
        .filter((f): f is DraftTemplateField => f !== null);

      return {
        ...prev,
        fields: [...orderedFields, ...deletedFields],
      };
    });
  }, []);

  // Reset to original state
  const reset = useCallback(() => {
    if (originalTemplate) {
      setDraftTemplate(serverTemplateToDraft(originalTemplate));
    } else {
      setDraftTemplate(createEmptyDraftTemplate(defaultType));
    }
  }, [originalTemplate, defaultType]);

  // Save all changes
  const save = useCallback(async (): Promise<boolean> => {
    if (isReadOnly) {
      toast.error("Cannot edit base templates");
      return false;
    }

    setSaving(true);
    setError(null);
    try {
      let currentTemplateId = draftTemplate.id;

      // Step 1: Create or update template
      if (!currentTemplateId) {
        // Create new template
        const createRequest = CreateTemplateRequestSchema.parse({
          name: draftTemplate.name,
          type: draftTemplate.type,
        });

        const response = await fetchFastApi({
          path: "/work-paper-templates/",
          method: "POST",
          body: createRequest,
        });

        const parsed = CreateTemplateResponseSchema.parse(response);
        currentTemplateId = parsed.templateId;
      } else if (draftTemplate.isModified) {
        // Update existing template name
        const updateRequest = UpdateTemplateRequestSchema.parse({
          name: draftTemplate.name,
        });

        await fetchFastApi({
          path: `/work-paper-templates/${currentTemplateId}`,
          method: "PATCH",
          body: updateRequest,
        });
      }

      // Step 2: Delete removed fields
      const deletedFields = draftTemplate.fields.filter(
        (f) => f.isDeleted && !f.isNew,
      );
      for (const field of deletedFields) {
        await fetchFastApi({
          path: `/work-paper-templates/${currentTemplateId}/fields/${field.id}`,
          method: "DELETE",
        });
      }

      // Step 3: Update modified fields
      const modifiedFields = draftTemplate.fields.filter(
        (f) => f.isModified && !f.isNew && !f.isDeleted,
      );
      for (const field of modifiedFields) {
        let updateRequest;

        if (field.fieldType === "static" && field.staticField) {
          updateRequest = UpdateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "static",
            staticField: {
              sheetName: field.staticField.sheetName,
              cellRow: field.staticField.cellAddress.row,
              cellColumn: field.staticField.cellAddress.column,
              source: field.staticField.source,
              sourceSelector: field.staticField.sourceSelector,
            },
          });
        } else if (field.fieldType === "dynamic" && field.computedField) {
          updateRequest = UpdateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "dynamic",
            computedField: {
              sheetName: field.computedField.sheetName,
              cellRow: field.computedField.cellAddress.row,
              cellColumn: field.computedField.cellAddress.column,
              context: field.computedField.context,
              instructions: field.computedField.instructions,
            },
          });
        } else if (
          field.fieldType === "attribute_summary_table" &&
          field.attributeSummaryTable
        ) {
          updateRequest = UpdateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "attribute_summary_table",
            attributeSummaryTable: field.attributeSummaryTable,
          });
        } else if (
          field.fieldType === "sample_summary_table" &&
          field.sampleSummaryTable
        ) {
          updateRequest = UpdateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "sample_summary_table",
            sampleSummaryTable: field.sampleSummaryTable,
          });
        } else if (
          field.fieldType === "detail_testing_table" &&
          field.detailTestingTable
        ) {
          updateRequest = UpdateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "detail_testing_table",
            detailTestingTable: field.detailTestingTable,
          });
        } else if (
          field.fieldType === "citation_asset_sheet" &&
          field.citationAssetSheet
        ) {
          updateRequest = UpdateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "citation_asset_sheet",
            citationAssetSheet: field.citationAssetSheet,
          });
        } else if (
          field.fieldType === "computed_table" &&
          field.computedTableField
        ) {
          updateRequest = UpdateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "computed_table",
            computedTableField: {
              sheetName: field.computedTableField.sheetName,
              startRow: field.computedTableField.startRow,
              headerRow: field.computedTableField.headerRow,
              columnStart: field.computedTableField.columnStart,
              context: field.computedTableField.context,
              instructions: field.computedTableField.instructions,
              columns: field.computedTableField.columns.map((col) => ({
                name: col.name,
                dataType: col.dataType,
                instructions: col.instructions,
                columnOrder: col.columnOrder,
              })),
            },
          });
        } else {
          continue;
        }

        await fetchFastApi({
          path: `/work-paper-templates/${currentTemplateId}/fields/${field.id}`,
          method: "PATCH",
          body: updateRequest,
        });
      }

      // Step 4: Create new fields
      const newFields = draftTemplate.fields.filter(
        (f) => f.isNew && !f.isDeleted,
      );
      const tempIdToRealId = new Map<string, string>();

      for (const field of newFields) {
        let createRequest;

        if (field.fieldType === "static" && field.staticField) {
          createRequest = CreateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "static",
            staticField: {
              sheetName: field.staticField.sheetName,
              cellRow: field.staticField.cellAddress.row,
              cellColumn: field.staticField.cellAddress.column,
              source: field.staticField.source,
              sourceSelector: field.staticField.sourceSelector,
            },
          });
        } else if (field.fieldType === "dynamic" && field.computedField) {
          createRequest = CreateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "dynamic",
            computedField: {
              sheetName: field.computedField.sheetName,
              cellRow: field.computedField.cellAddress.row,
              cellColumn: field.computedField.cellAddress.column,
              context: field.computedField.context,
              instructions: field.computedField.instructions,
            },
          });
        } else if (
          field.fieldType === "attribute_summary_table" &&
          field.attributeSummaryTable
        ) {
          createRequest = CreateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "attribute_summary_table",
            attributeSummaryTable: field.attributeSummaryTable,
          });
        } else if (
          field.fieldType === "sample_summary_table" &&
          field.sampleSummaryTable
        ) {
          createRequest = CreateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "sample_summary_table",
            sampleSummaryTable: field.sampleSummaryTable,
          });
        } else if (
          field.fieldType === "detail_testing_table" &&
          field.detailTestingTable
        ) {
          createRequest = CreateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "detail_testing_table",
            detailTestingTable: field.detailTestingTable,
          });
        } else if (
          field.fieldType === "citation_asset_sheet" &&
          field.citationAssetSheet
        ) {
          createRequest = CreateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "citation_asset_sheet",
            citationAssetSheet: field.citationAssetSheet,
          });
        } else if (
          field.fieldType === "computed_table" &&
          field.computedTableField
        ) {
          createRequest = CreateFieldRequestSchema.parse({
            name: field.name,
            fieldType: "computed_table",
            computedTableField: {
              sheetName: field.computedTableField.sheetName,
              startRow: field.computedTableField.startRow,
              headerRow: field.computedTableField.headerRow,
              columnStart: field.computedTableField.columnStart,
              context: field.computedTableField.context,
              instructions: field.computedTableField.instructions,
              columns: field.computedTableField.columns.map((col) => ({
                name: col.name,
                dataType: col.dataType,
                instructions: col.instructions,
                columnOrder: col.columnOrder,
              })),
            },
          });
        } else {
          continue;
        }

        const response = await fetchFastApi({
          path: `/work-paper-templates/${currentTemplateId}/fields`,
          method: "POST",
          body: createRequest,
        });

        const parsed = CreateFieldResponseSchema.parse(response);
        tempIdToRealId.set(field.id, parsed.fieldId);
      }

      // Step 5: Reorder all fields
      const activeFields = draftTemplate.fields.filter((f) => !f.isDeleted);
      if (activeFields.length > 0) {
        const reorderRequest: ReorderFieldsRequest = {
          fieldUpdates: activeFields.map((field) => ({
            fieldId: tempIdToRealId.get(field.id) || field.id,
            fieldOrder: field.fieldOrder,
          })),
        };

        await fetchFastApi({
          path: `/work-paper-templates/${currentTemplateId}/fields/reorder`,
          method: "POST",
          body: reorderRequest,
        });
      }

      toast.success("Template saved successfully");

      // Reload the template from server to get fresh state
      if (currentTemplateId) {
        await loadTemplate(currentTemplateId);
      }

      if (onSaveSuccess) {
        onSaveSuccess(currentTemplateId);
      }

      return true;
    } catch (error) {
      console.error("Error saving template:", error);
      const errorMsg = refusalDetail(error) ?? "Failed to save template";
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    } finally {
      setSaving(false);
    }
  }, [draftTemplate, isReadOnly, loadTemplate, onSaveSuccess]);

  // Load template on mount if templateId provided
  useEffect(() => {
    if (templateId) {
      loadTemplate(templateId);
    }
  }, [templateId, loadTemplate]);

  return {
    loading,
    saving,
    error,
    draftTemplate,
    hasUnsavedChanges,
    isReadOnly,
    updateName,
    updateType,
    addField,
    updateField,
    deleteField,
    reorderFields,
    save,
    reset,
    loadTemplate,
    clearError,
  };
}
